import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { Helmet } from "react-helmet-async";
import { useAuth } from "@/hooks/use-auth";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { 
  Building2, 
  TrendingUp, 
  Users, 
  Heart, 
  Shield, 
  LogOut, 
  Trash2, 
  BadgeCheck, 
  AlertCircle,
  Edit2,
  Phone,
  MessageCircle,
  Eye,
  Star
} from "lucide-react";
import { CadastroFornecedorForm } from "@/components/fornecedores/CadastroFornecedorForm";
import { z } from "zod";
import { cn } from "@/lib/utils";


const accountSearchSchema = z.object({
  aba: z.string().optional().catch("dados"),
});

export const Route = createFileRoute("/_authenticated/minha-conta")({
  validateSearch: (search) => accountSearchSchema.parse(search),
  component: MinhaConta,
});

function MinhaConta() {
  const { profile, user, refreshProfile, signOut } = useAuth();
  const search = Route.useSearch();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  
  const [nome, setNome] = useState(profile?.nome || "");
  const [telefone, setTelefone] = useState(profile?.telefone || "");
  const [selectedSegmentos, setSelectedSegmentos] = useState<string[]>(profile?.segmentos_interesse || []);
  const [loading, setLoading] = useState(false);
  const [isEditingForm, setIsEditingForm] = useState(false);

  // Sync state with profile when it loads
  useEffect(() => {
    if (profile) {
      setNome(profile.nome || "");
      setTelefone(profile.telefone || "");
      setSelectedSegmentos(profile.segmentos_interesse || []);
    }
  }, [profile]);

  // Carregar segmentos disponíveis
  const { data: segmentos = [] } = useQuery({
    queryKey: ['segmentos-minha-conta'],
    queryFn: async () => {
      const { data, error } = await supabase.from('segmentos').select('*').eq('ativo', true).order('nome');
      if (error) throw error;
      return data;
    }
  });

  // Carregar dados da empresa (se houver) e estatísticas
  const { data: empresa, isLoading: isLoadingEmpresa } = useQuery({
    queryKey: ['minha-empresa', user?.id],
    queryFn: async () => {
      const { data: fData, error } = await supabase
        .from('fornecedores')
        .select('*, segmentos(nome, slug, icone)')
        .eq('owner_id', user?.id)
        .maybeSingle();
      
      if (error) throw error;
      if (!fData) return null;

      // Get stats via rpc if available, fallback to manual counts
      // Note: Direct counts on 'eventos' might return 0 for non-admins due to RLS
      const { data: metricsData } = await (supabase.rpc as any)('metricas_minha_ficha');
      
      const stats = {
        favoritos: (metricsData as any)?.favoritado || 0,
        visualizacoes: (metricsData as any)?.visualizacoes || 0,
        cliques_whatsapp: (metricsData as any)?.cliques_whatsapp || 0,
        cliques_site: (metricsData as any)?.cliques_site || 0
      };

      return {
        ...fData,
        stats
      };
    },
    enabled: !!user?.id
  });

  const handleUpdate = async () => {
    setLoading(true);
    const { error } = await supabase
      .from('profiles')
      .update({ 
        nome, 
        telefone, 
        segmentos_interesse: selectedSegmentos 
      })
      .eq('id', user.id);
    
    if (error) {
      toast.error("Erro ao atualizar");
    } else {
      toast.success("Dados salvos com sucesso!");
      refreshProfile();
    }
    setLoading(false);
  };

  const handleToggleSegmento = (slug: string) => {
    setSelectedSegmentos(prev => 
      prev.includes(slug) ? prev.filter(s => s !== slug) : [...prev, slug]
    );
  };

  const activeTab = search.aba || "dados";

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl font-poppins">
      <Helmet>
        <title>Minha Conta | Gopage</title>
      </Helmet>
      
      <h1 className="text-3xl font-bold mb-8 text-[#0C1A12]">Minha Conta</h1>
      
      <Tabs 
        value={activeTab} 
        onValueChange={(value) => navigate({ search: { aba: value } as any })}
        className="space-y-6"
      >
        <TabsList className="bg-transparent border-b rounded-none w-full justify-start gap-8 h-auto p-0 overflow-x-auto scrollbar-none">
          <TabsTrigger value="dados" className="px-0 pb-3 border-b-2 border-transparent data-[state=active]:border-[#C6F764] data-[state=active]:text-[#0C1A12] rounded-none font-bold text-muted-foreground transition-all shrink-0">Meus Dados</TabsTrigger>
          <TabsTrigger value="empresa" className="px-0 pb-3 border-b-2 border-transparent data-[state=active]:border-[#C6F764] data-[state=active]:text-[#0C1A12] rounded-none font-bold text-muted-foreground transition-all shrink-0">Minha Empresa</TabsTrigger>
          <TabsTrigger value="seguranca" className="px-0 pb-3 border-b-2 border-transparent data-[state=active]:border-[#C6F764] data-[state=active]:text-[#0C1A12] rounded-none font-bold text-muted-foreground transition-all shrink-0">Segurança</TabsTrigger>
          <TabsTrigger value="favoritos" asChild>
            <Link to="/favoritos" className="px-0 pb-3 border-b-2 border-transparent hover:text-[#0C1A12] rounded-none font-bold text-muted-foreground transition-all shrink-0 flex items-center">
              Favoritos
            </Link>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="dados">
          <Card className="rounded-3xl p-8 shadow-sm border-gray-100">
            <div className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Nome completo</label>
                  <Input 
                    value={nome} 
                    onChange={(e) => setNome(e.target.value)} 
                    className="h-12 rounded-xl bg-secondary/30 border-transparent focus:bg-white focus:ring-primary"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">WhatsApp</label>
                  <Input 
                    value={telefone} 
                    onChange={(e) => setTelefone(e.target.value)} 
                    className="h-12 rounded-xl bg-secondary/30 border-transparent focus:bg-white focus:ring-primary"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">E-mail</label>
                <Input disabled value={user?.email || ""} className="h-12 rounded-xl bg-secondary/10 border-transparent opacity-60" />
                <p className="text-[11px] text-muted-foreground flex items-center gap-1.5">
                  <AlertCircle className="w-3.5 h-3.5" /> É por este e-mail que confirmamos seu acesso Gopage.
                </p>
              </div>
              
              <div className="space-y-4 pt-6 border-t">
                <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Segmentos de interesse</label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {segmentos.map(seg => (
                    <div 
                      key={seg.id} 
                      className={`flex items-center space-x-2 p-3 rounded-xl border transition-all cursor-pointer ${selectedSegmentos.includes(seg.slug) ? 'bg-[#C6F764]/10 border-[#C6F764] shadow-sm shadow-[#C6F764]/10' : 'hover:bg-secondary/50 border-gray-100'}`}
                      onClick={() => handleToggleSegmento(seg.slug)}
                    >
                      <Checkbox 
                        checked={selectedSegmentos.includes(seg.slug)}
                        onCheckedChange={() => handleToggleSegmento(seg.slug)}
                        className="rounded-[4px] border-gray-300 data-[state=checked]:bg-[#C6F764] data-[state=checked]:border-[#C6F764] data-[state=checked]:text-[#0C1A12]"
                      />
                      <span className="text-sm font-medium">{seg.nome}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="pt-4">
                <Button onClick={handleUpdate} disabled={loading} className="bg-[#0C1A12] text-white hover:bg-black rounded-xl h-14 px-12 font-bold shadow-lg shadow-[#0C1A12]/10 transition-transform active:scale-[0.98]">
                  {loading ? "Salvando..." : "Salvar alterações"}
                </Button>
              </div>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="empresa" className="animate-in fade-in slide-in-from-bottom-2 duration-300">
          {!isLoadingEmpresa && (
            empresa && !isEditingForm ? (
              <div className="space-y-6">
                {/* Status Card */}
                <Card className="rounded-[32px] p-8 shadow-sm border-gray-100">
                  <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-8">
                    <div className="flex gap-6 items-center">
                      <div className="w-20 h-20 bg-secondary/30 rounded-2xl flex items-center justify-center shrink-0">
                        {empresa.logo_url ? (
                          <img src={empresa.logo_url} alt={empresa.nome} className="w-full h-full object-contain p-2" />
                        ) : (
                          <Building2 className="w-10 h-10 text-muted-foreground" />
                        )}
                      </div>
                      <div>
                        <div className="flex items-center gap-3 mb-1">
                          <h3 className="text-2xl font-bold text-[#0C1A12]">{empresa.nome}</h3>
                          <Badge className={cn(
                            "rounded-full px-3 py-1 font-bold text-[10px] uppercase tracking-wider",
                            empresa.status === 'aprovado' ? 'bg-[#C6F764] text-[#0C1A12]' : 
                            empresa.status === 'pendente' ? 'bg-amber-100 text-amber-700' : 
                            'bg-red-100 text-red-700'
                          )}>
                            {empresa.status === 'aprovado' ? 'Aprovado' : 
                             empresa.status === 'pendente' ? 'Pendente' : 'Rejeitado'}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <span>{empresa.segmentos?.nome}</span>
                          <span className="w-1 h-1 bg-gray-300 rounded-full" />
                          <span>{empresa.cidade}/{empresa.uf}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-3 w-full md:w-auto">
                      <Button 
                        variant="outline" 
                        className="rounded-xl font-bold flex-1 md:flex-none h-12"
                        onClick={() => {
                          if (confirm("Ao editar, sua ficha voltará para moderação. Deseja continuar?")) {
                            setIsEditingForm(true);
                          }
                        }}
                      >
                        <Edit2 className="w-4 h-4 mr-2" /> Editar dados
                      </Button>
                      <Button asChild variant="onLight" className="rounded-xl font-bold flex-1 md:flex-none h-12 bg-[#C6F764] text-[#0C1A12] hover:bg-[#C6F764]/90">
                        <Link to="/fornecedores/$id" params={{ id: empresa.id }}>Ver perfil público</Link>
                      </Button>
                    </div>
                  </div>

                  {empresa.status === 'rejeitado' && empresa.motivo_rejeicao && (
                    <div className="mb-8 p-4 bg-red-50 border border-red-100 rounded-2xl flex gap-3 text-red-800 text-sm">
                      <AlertCircle className="w-5 h-5 shrink-0" />
                      <div>
                        <p className="font-bold">Motivo da rejeição:</p>
                        <p>{empresa.motivo_rejeicao}</p>
                        <Button 
                          variant="link" 
                          className="p-0 h-auto text-red-800 font-bold underline mt-2"
                          onClick={() => setIsEditingForm(true)}
                        >
                          Corrigir e reenviar
                        </Button>
                      </div>
                    </div>
                  )}

                  {!empresa.verificado && empresa.status !== 'rejeitado' && (
                    <div className="mb-8 p-6 bg-[#C6F764]/10 border border-[#C6F764]/20 rounded-2xl flex gap-4 items-start">
                      <BadgeCheck className="w-6 h-6 text-[#0C1A12] shrink-0" />
                      <div>
                        <h4 className="font-bold text-[#0C1A12] mb-1">Sua ficha ainda não tem o selo de Verificado</h4>
                        <p className="text-sm text-[#0C1A12]/70">Ele é concedido pela nossa equipe após a conferência do CNPJ e dados da empresa.</p>
                      </div>
                    </div>
                  )}

                  {/* Metrics Block */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Card className="p-5 bg-secondary/20 border-none rounded-2xl">
                      <div className="flex items-center gap-2 text-muted-foreground mb-1">
                        <Eye className="w-4 h-4" />
                        <span className="text-[10px] font-bold uppercase tracking-wider">Visualizações</span>
                      </div>
                      <div className="flex flex-col">
                        <span className="text-2xl font-black text-[#0C1A12]">{empresa.stats?.visualizacoes || 0}</span>
                        <span className="text-[10px] text-muted-foreground mt-1">Últimos 30 dias</span>
                      </div>
                    </Card>
                    <Card className="p-5 bg-secondary/20 border-none rounded-2xl">
                      <div className="flex items-center gap-2 text-muted-foreground mb-1">
                        <MessageCircle className="w-4 h-4" />
                        <span className="text-[10px] font-bold uppercase tracking-wider">Cliques WhatsApp</span>
                      </div>
                      <div className="flex flex-col">
                        <span className="text-2xl font-black text-[#0C1A12]">{empresa.stats?.cliques_whatsapp || 0}</span>
                        <span className="text-[10px] text-muted-foreground mt-1">Últimos 30 dias</span>
                      </div>
                    </Card>
                    <Card className="p-5 bg-secondary/20 border-none rounded-2xl">
                      <div className="flex items-center gap-2 text-muted-foreground mb-1">
                        <TrendingUp className="w-4 h-4" />
                        <span className="text-[10px] font-bold uppercase tracking-wider">Cliques Site</span>
                      </div>
                      <div className="flex flex-col">
                        <span className="text-2xl font-black text-[#0C1A12]">{empresa.stats?.cliques_site || 0}</span>
                        <span className="text-[10px] text-muted-foreground mt-1">Últimos 30 dias</span>
                      </div>
                    </Card>
                    <Card className="p-5 bg-secondary/20 border-none rounded-2xl">
                      <div className="flex items-center gap-2 text-muted-foreground mb-1">
                        <Star className="w-4 h-4" />
                        <span className="text-[10px] font-bold uppercase tracking-wider">Favoritado</span>
                      </div>
                      <div className="flex flex-col">
                        <span className="text-2xl font-black text-[#0C1A12]">{empresa.stats?.favoritos || 0}</span>
                        <span className="text-[10px] text-muted-foreground mt-1">Total de vezes</span>
                      </div>
                    </Card>
                  </div>
                  
                  <div className="mt-6 text-center">
                    <p className="text-xs text-muted-foreground">Os números aparecem conforme os lojistas visitam sua ficha.</p>
                  </div>
                </Card>
              </div>
            ) : empresa || isEditingForm ? (
              <Card className="rounded-[32px] p-8 shadow-sm border-gray-100 bg-white">
                <div className="mb-8 flex justify-between items-center">
                  <div>
                    <h2 className="text-2xl font-bold text-[#0C1A12]">Dados da Empresa</h2>
                    <p className="text-sm text-muted-foreground">Mantenha sua ficha atualizada para vender mais.</p>
                  </div>
                  {isEditingForm && empresa && (
                    <Button variant="ghost" className="rounded-xl font-bold text-muted-foreground" onClick={() => setIsEditingForm(false)}>
                      Cancelar
                    </Button>
                  )}
                </div>
                <CadastroFornecedorForm 
                  initialData={empresa} 
                  onSuccess={() => {
                    setIsEditingForm(false);
                    queryClient.invalidateQueries({ queryKey: ['minha-empresa', user?.id] });
                  }}
                  onCancel={empresa ? () => setIsEditingForm(false) : (undefined as any)}
                />


              </Card>
            ) : (
              <Card className="rounded-[40px] p-12 text-center border-dashed border-2 border-gray-200 bg-white shadow-xl shadow-gray-100/20">
                <div className="w-24 h-24 bg-secondary/30 rounded-[32px] flex items-center justify-center mx-auto mb-8 shadow-inner">
                  <Building2 className="w-12 h-12 text-muted-foreground" />
                </div>
                <h2 className="text-3xl font-bold mb-4 text-[#0C1A12]">Sua empresa ainda não está na lista</h2>
                <p className="text-gray-500 mb-10 max-w-md mx-auto leading-relaxed">
                  Cadastre sua empresa como fornecedor para <span className="text-[#0C1A12] font-semibold">aparecer para os lojistas que usam a Lista de Fornecedores.</span>
                </p>
                <Button 
                  onClick={() => setIsEditingForm(true)} 
                  className="bg-[#0C1A12] text-white hover:bg-black rounded-2xl px-12 h-16 text-lg font-bold shadow-2xl shadow-[#0C1A12]/20 transition-transform active:scale-[0.98]"
                >
                  Cadastrar minha empresa
                </Button>
              </Card>
            )
          )}
        </TabsContent>

        <TabsContent value="seguranca">
          <Card className="rounded-3xl p-8 shadow-sm border-gray-100">
            <div className="space-y-8">
              <div className="flex items-center gap-5">
                <div className="w-14 h-14 bg-secondary/30 rounded-2xl flex items-center justify-center shrink-0">
                  <Shield className="w-7 h-7 text-[#0C1A12]" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-[#0C1A12]">Segurança e Acesso</h3>
                  <p className="text-sm text-muted-foreground">Gerencie sua senha e sessões ativas.</p>
                </div>
              </div>
              
              <div className="grid gap-4">
                <div className="flex items-center justify-between p-6 rounded-2xl border border-gray-100 bg-gray-50/30">
                  <div>
                    <h4 className="font-bold text-[#0C1A12]">Senha</h4>
                    <p className="text-sm text-muted-foreground">Altere sua senha de acesso periodicamente.</p>
                  </div>
                  <Button variant="outline" className="rounded-xl font-bold h-11 px-6" onClick={() => toast.info("Funcionalidade em desenvolvimento.")}>Alterar senha</Button>
                </div>

                <div className="flex items-center justify-between p-6 rounded-2xl border border-gray-100 bg-gray-50/30">
                  <div>
                    <h4 className="font-bold text-[#0C1A12]">Sessões ativas</h4>
                    <p className="text-sm text-muted-foreground">Você está logado neste dispositivo</p>
                  </div>
                  <Button variant="outline" className="rounded-xl font-bold h-11 px-6" onClick={() => toast.info("Sessão atual encerrada.")}>Desconectar outros</Button>
                </div>
              </div>

              <div className="mt-8 pt-8 border-t border-gray-100 flex flex-col md:flex-row gap-6 items-center justify-between">
                <div>
                  <h4 className="font-bold text-destructive flex items-center gap-2">
                    <Trash2 className="w-4 h-4" /> Zona de perigo
                  </h4>
                  <p className="text-sm text-muted-foreground">Ações irreversíveis para sua conta de acesso.</p>
                </div>
                <div className="flex gap-4">
                  <Button 
                    variant="ghost" 
                    className="text-[#6B7280] font-bold h-12 rounded-xl"
                    onClick={() => signOut()}
                  >
                    <LogOut className="w-4 h-4 mr-2" /> Sair da conta
                  </Button>
                  <Button variant="ghost" className="text-destructive hover:bg-red-50 hover:text-red-700 rounded-xl font-bold h-12 px-6" onClick={() => toast.warning("Confirmação dupla necessária. Fale com o suporte.")}>
                    Excluir minha conta
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

